/**
 * 键盘导航 Hook
 * 处理树节点的键盘操作
 */

import { useCallback, useRef } from 'react';
import React from 'react';
import { KeyCode } from '../../../wui-core/src';
import { loopAllChildrenSimple } from '../utils/util';

interface UseKeyboardNavProps {
    expandedKeys: string[];
    selectedKeys: string[];
    focusKey: string;
    clsPrefix?: string;
    autoSelectWhenFocus?: boolean;
    checkable?: boolean;
    showLine?: boolean;
    onExpand: (treeNode: React.ReactElement, keyType?: string, e?: React.KeyboardEvent) => void;
    onSelect: (treeNode: React.ReactElement, e: React.KeyboardEvent) => void;
    onCheck?: (treeNode: React.ReactElement) => void;
    onDoubleClick?: (treeNode: React.ReactElement) => void;
    setFocusKey: (key: string) => void;
    keyFun?: (e: React.KeyboardEvent, treeNode: React.ReactElement) => void;
    children?: React.ReactNode;
    cacheTreeNodes?: React.ReactElement[];
    onFocus?: (isExist: boolean) => Promise<unknown> | void;
}

interface UseKeyboardNavReturn {
    handleKeyDown: (e: React.KeyboardEvent, treeNode: React.ReactElement) => void;
    handleUlFocus: (e: React.FocusEvent<HTMLUListElement>) => void;
}

/**
 * 键盘导航 Hook
 */
export function useKeyboardNav(props: UseKeyboardNavProps): UseKeyboardNavReturn {
    const {
        expandedKeys,
        selectedKeys,
        // focusKey,
        clsPrefix = 'rc-tree',
        autoSelectWhenFocus = false,
        checkable = false,
        showLine = false,
        onExpand,
        onSelect,
        onCheck,
        onDoubleClick,
        setFocusKey,
        keyFun,
        children,
        cacheTreeNodes,
        onFocus
    } = props;

    const selectKeyDomPosRef = useRef<string>('0-0');
    const selectKeyDomExistRef = useRef<boolean>(false);

    /**
     * 向下导航
     */
    const goDown = useCallback((
        currentPos: string,
        currentIndex: string,
        e: React.KeyboardEvent,
        treeNode: React.ReactElement
    ) => {
        const treeChildren = children ? children : cacheTreeNodes;
        const nextIndex = parseInt(currentIndex) + 1;

        let nextPos: string;
        let nextTreeNode: React.ReactElement | undefined;
        const backNextPosArr: string[] = [];
        const backNextTreeNodeArr: React.ReactElement[] = [];
        const tempBackNextPosArr: string[] = [];

        // 是否为展开的节点，如果展开获取第一个子节点的信息
        if (expandedKeys.indexOf(treeNode.props.eventKey) > -1) {
            nextPos = currentPos + '-0';
        } else {
            nextPos = currentPos.substr(0, currentPos.lastIndexOf('-') + 1) + nextIndex;
        }

        // 若向下的节点没有了，找到父级相邻节点
        let tempPosArr = currentPos.split('-');
        let tempPosArrLength = tempPosArr.length;

        // 将可能是下一个节点的位置都备份一遍
        while (tempPosArrLength > 1) {
            const backNextPos = tempPosArrLength > 1
                ? tempPosArr.slice(0, tempPosArrLength - 1).join('-') + '-' + (parseInt(tempPosArr[tempPosArrLength - 1]) + 1)
                : '';
            tempBackNextPosArr.push(backNextPos);
            tempPosArr = tempPosArr.slice(0, tempPosArrLength - 1);
            tempPosArrLength = tempPosArr.length;
        }

        // 选中下一个相邻的节点
        loopAllChildrenSimple(treeChildren as React.ReactElement[], (itemNode: React.ReactElement, _index?: number, pos?: string) => {
            if (pos === nextPos) {
                nextTreeNode = itemNode;
            }
            tempBackNextPosArr.forEach(item => {
                if (item && item === pos) {
                    backNextTreeNodeArr.push(itemNode);
                    backNextPosArr.push(pos);
                }
            });
        }, true);

        // 如果没有下一个节点，则获取父节点的下一个节点
        if (!nextTreeNode) {
            for (let i = 0; i < backNextTreeNodeArr.length; i++) {
                if (backNextTreeNodeArr[i]) {
                    nextTreeNode = backNextTreeNodeArr[i];
                    nextPos = backNextPosArr[i];
                    break;
                }
            }
        }

        // 查询的下一个节点不为空的话，则选中
        if (nextTreeNode) {
            const queryInfo = `a[pos="${nextPos}"]`;
            const parentEle = closest(e.target as HTMLElement, `.${clsPrefix}`);
            const focusEle = parentEle ? parentEle.querySelector(queryInfo) as HTMLElement : null;
            focusEle && focusEle.focus();

            const eventKey = nextTreeNode.props.eventKey || nextTreeNode.key;
            setFocusKey(eventKey as string);

            if (autoSelectWhenFocus) {
                onSelect(nextTreeNode, e);
            }
        } else {
            setDataTransfer(e);
        }
    }, [expandedKeys, children, cacheTreeNodes, clsPrefix, autoSelectWhenFocus, onSelect, setFocusKey]);

    /**
     * 向上导航
     */
    const goUp = useCallback((
        currentPos: string,
        currentIndex: string,
        e: React.KeyboardEvent
    ) => {
        if (currentIndex === "0" && currentPos.length === 3) {
            setDataTransfer(e);
            return;
        }

        const preIndex = parseInt(currentIndex) - 1;
        let prePos: string;

        if (preIndex >= 0) {
            prePos = currentPos.substr(0, currentPos.lastIndexOf('-') + 1) + preIndex;
        } else {
            prePos = currentPos.substr(0, currentPos.lastIndexOf('-'));
        }

        let prevTreeNode: React.ReactElement | undefined;
        let preElement: HTMLElement | undefined | null;
        const treeNodes = children || cacheTreeNodes;

        // 选中上一个相邻的节点
        loopAllChildrenSimple(treeNodes as React.ReactElement[], (item: React.ReactElement, _index?: number, pos?: string) => {
            if (pos === prePos) {
                prevTreeNode = item;
            }
        }, true);

        // 查询的上一个节点不为空的话，则选中
        if (prevTreeNode) {
            const eventTarget = e.target as HTMLElement;

            if (preIndex >= 0) {
                // 如果上面的节点展开则默认选择最后一个子节点
                if (expandedKeys.indexOf(prevTreeNode.key as string) > -1) {
                    const selector = showLine
                        ? `.${clsPrefix}-node-content-wrapper`
                        : `.${clsPrefix}-node-content-wrapper`;
                    const preElementArr = eventTarget?.parentElement?.parentElement?.previousElementSibling?.querySelectorAll(selector);

                    if (preElementArr) {
                        preElement = preElementArr[preElementArr.length - 1] as HTMLElement;
                        prePos = preElement?.getAttribute('pos') || '';

                        loopAllChildrenSimple(treeNodes as React.ReactElement[], (item: React.ReactElement, _index?: number, pos?: string) => {
                            if (pos === prePos) {
                                prevTreeNode = item;
                            }
                        }, true);
                    }
                } else {
                    // 上一个节点没有展开
                    const selector = `.${clsPrefix}-node-content-wrapper`;
                    preElement = eventTarget?.parentElement?.parentElement?.previousElementSibling?.querySelector(selector) as HTMLElement;
                }
            } else {
                // 不存在上一个节点时，选中它的父节点
                const selector = `.${clsPrefix}-node-content-wrapper`;
                const parentNode = eventTarget?.parentElement?.parentElement?.parentElement?.parentElement?.parentElement;
                preElement = parentNode?.querySelector(selector) as HTMLElement;
            }
        } else {
            setDataTransfer(e);
        }

        if (!preElement) {
            setDataTransfer(e);
        }

        preElement && preElement.focus();

        if (prevTreeNode) {
            const eventKey = prevTreeNode.props.eventKey || prevTreeNode.key;
            setFocusKey(eventKey as string);

            if (autoSelectWhenFocus) {
                onSelect(prevTreeNode, e);
            }
        }
    }, [expandedKeys, children, cacheTreeNodes, clsPrefix, showLine, autoSelectWhenFocus, onSelect, setFocusKey]);

    /**
     * 处理键盘按下事件
     */
    const handleKeyDown = useCallback((e: React.KeyboardEvent, treeNode: React.ReactElement) => {
        const currentPos = treeNode.props.pos;
        const selectable = treeNode.props.selectable;
        const currentIndex = currentPos.substr(currentPos.lastIndexOf('-') + 1);

        // 向下键
        if (e.keyCode === KeyCode.DOWN) {
            goDown(currentPos, currentIndex, e, treeNode);
        } else if (e.keyCode === KeyCode.UP) {
            goUp(currentPos, currentIndex, e);
        } else if (e.keyCode === KeyCode.LEFT && !treeNode.props.isLeaf) { // 左键 - 收起树节点
            onExpand(treeNode, 'left', e);
        } else if (e.keyCode === KeyCode.RIGHT && !treeNode.props.isLeaf) { // 右键 - 展开树节点
            onExpand(treeNode, 'right', e);
        } else if (e.keyCode === KeyCode.SPACE) {
            onSelect(treeNode, e);
            // 如果是多选tree则进行选中或者反选该节点
            checkable && onCheck && onCheck(treeNode);
        } else if (e.keyCode === KeyCode.ENTER) {
            if (onDoubleClick) {
                onDoubleClick(treeNode);
            } else {
                selectable && onSelect(treeNode, e);
                checkable && onCheck && onCheck(treeNode);
            }
        }

        keyFun && keyFun(e, treeNode);
    }, [goDown, goUp, onExpand, onSelect, onCheck, onDoubleClick, checkable, keyFun]);

    /**
     * 处理UL获得焦点事件
     */
    const handleUlFocus = useCallback((e: React.FocusEvent<HTMLUListElement>) => {
        const targetDom = e.currentTarget;

        // 如果当前树的根节点不包含上一个焦点节点，则触发（与类组件逻辑保持一致）
        if (e.target == targetDom && !targetDom.contains(e.relatedTarget as Node | null)) {
            let treeChildren = children || cacheTreeNodes;
            if (treeChildren && !Array.isArray(treeChildren)) {
                treeChildren = [treeChildren];
            }

            const tabIndexKey = selectedKeys[0];
            const treeNode = treeChildren && (treeChildren as React.ReactElement[]).length && (treeChildren as React.ReactElement[])[0];
            const eventKey = treeNode && (treeNode.props.eventKey || treeNode.key);

            let isExist = false;
            if ((selectKeyDomExistRef.current && tabIndexKey) || !tabIndexKey) {
                isExist = true;
                const queryInfo = `a[pos="${selectKeyDomPosRef.current}"]`;
                const parentEle = closest(e.target as HTMLElement, `.${clsPrefix}`);
                const focusEle = parentEle ? parentEle.querySelector(queryInfo) as HTMLElement : null;
                focusEle && focusEle.focus();

                // TAB键选中树后，默认聚焦在第一个（已选中）节点，并显示 focus 状态
                setFocusKey(tabIndexKey || eventKey as string);
            }

            // 调用onFocus回调
            const onFocusRes = onFocus && onFocus(isExist);
            if (onFocusRes instanceof Promise) {
                onFocusRes.then(() => {
                    focusDom(selectKeyDomPosRef.current, targetDom as HTMLElement);
                });
            } else {
                focusDom(selectKeyDomPosRef.current, targetDom as HTMLElement);
            }
        }
    }, [children, cacheTreeNodes, selectedKeys, clsPrefix, setFocusKey, onFocus]);

    return {
        handleKeyDown,
        handleUlFocus
    };
}

// 辅助函数已移动到 util.ts

function closest(el: HTMLElement, selector: string): HTMLElement | null {
    const matchesSelector = el.matches || (el as any).webkitMatchesSelector || (el as any).mozMatchesSelector || (el as any).msMatchesSelector;

    while (el) {
        if (matchesSelector.call(el, selector)) {
            return el;
        }
        el = el.parentElement as HTMLElement;
    }
    return null;
}

function setDataTransfer(e: React.KeyboardEvent): void {
    (e.target as any)._dataTransfer = {
        ooo: 'bee-tree',
        _cancelBubble: false
    };
}

function focusDom(selectKeyDomPos: string, targetDom: HTMLElement): void {
    const queryInfo = `a[pos="${selectKeyDomPos}"]`;
    const focusEle = targetDom.querySelector(queryInfo) as HTMLElement;
    if (document.activeElement !== focusEle) {
        focusEle && focusEle.focus();
    }
}